import { z } from 'zod'

import { AssetCampaignTagSchema, AssetSchema, CompleteCreatorMetadataSchema } from './asset.ts'
import {
  AnalysisStatusEnum,
  AssetAnalysisCategoryEnum,
  AssetAnalysisCoordinatesSchema,
  AssetAnalysisResponseSchema
} from './asset-analysis.ts'
import { idSchema, msTimestampSchema, MutationIdSchema } from './common.ts'
import { DeliveryHistoryEntrySchema } from './delivery.ts'

export const TriageStateEnum = z.enum(['reading', 'needs_confirmation', 'ready', 'held_back', 'rejected'])
export type TriageState = z.infer<typeof TriageStateEnum>

export const CategorySourceEnum = z.enum(['nova', 'creator', 'manual'])
export type CategorySource = z.infer<typeof CategorySourceEnum>

export const ManualAssetResolutionInputSchema = z
  .object({
    category: AssetAnalysisCategoryEnum,
    shortDescription: z.string().trim().min(1).max(500)
  })
  .strict()
export type ManualAssetResolutionInput = z.infer<typeof ManualAssetResolutionInputSchema>

export const CategoryOverrideRequestSchema = z.object({ category: AssetAnalysisCategoryEnum }).strict()
export type CategoryOverrideRequest = z.infer<typeof CategoryOverrideRequestSchema>

export const CategoryAmendmentRequestSchema = CategoryOverrideRequestSchema.extend({ mutationId: MutationIdSchema }).strict()
export type CategoryAmendmentRequest = z.infer<typeof CategoryAmendmentRequestSchema>

export const ManualAssetResolutionSchema = ManualAssetResolutionInputSchema.extend({
  workspaceId: idSchema,
  assetId: idSchema,
  source: z.literal(CategorySourceEnum.enum.manual),
  resolvedAt: msTimestampSchema
})
export type ManualAssetResolution = z.infer<typeof ManualAssetResolutionSchema>

export const CreatorCategoryOverrideSchema = z
  .object({
    workspaceId: idSchema,
    assetId: idSchema,
    generation: z.number().int().positive(),
    attempt: z.number().int().positive(),
    category: AssetAnalysisCategoryEnum,
    source: z.literal(CategorySourceEnum.enum.creator),
    updatedAt: msTimestampSchema
  })
  .strict()
export type CreatorCategoryOverride = z.infer<typeof CreatorCategoryOverrideSchema>

export const AssetAnalysisConfirmationSchema = AssetAnalysisCoordinatesSchema.extend({
  workspaceId: idSchema,
  assetId: idSchema,
  category: AssetAnalysisCategoryEnum,
  creatorMetadata: CompleteCreatorMetadataSchema,
  createdAt: msTimestampSchema
}).strict()
export type AssetAnalysisConfirmation = z.infer<typeof AssetAnalysisConfirmationSchema>

export const CreatorAmendmentPatchSchema = z
  .object({
    ...CompleteCreatorMetadataSchema.shape,
    category: AssetAnalysisCategoryEnum
  })
  .partial()
  .strict()
  .refine((patch) => Object.values(patch).some((value) => value !== undefined), {
    message: 'patch must include at least one field'
  })
export type CreatorAmendmentPatch = z.infer<typeof CreatorAmendmentPatchSchema>

export const CreatorAmendmentSchema = AssetAnalysisCoordinatesSchema.extend({
  id: idSchema,
  workspaceId: idSchema,
  assetId: idSchema,
  patch: CreatorAmendmentPatchSchema,
  createdAt: msTimestampSchema
}).strict()
export type CreatorAmendment = z.infer<typeof CreatorAmendmentSchema>

export const AcceptAssetRequestSchema = AssetAnalysisCoordinatesSchema.extend({
  category: AssetAnalysisCategoryEnum,
  creatorMetadata: CompleteCreatorMetadataSchema
}).strict()
export type AcceptAssetRequest = z.infer<typeof AcceptAssetRequestSchema>

export const AssetTriageProjectionSchema = z
  .object({
    state: TriageStateEnum,
    category: AssetAnalysisCategoryEnum.nullable(),
    categorySource: CategorySourceEnum.nullable(),
    manualResolution: ManualAssetResolutionSchema.nullable(),
    creatorCategoryOverride: CreatorCategoryOverrideSchema.nullable(),
    creatorCategoryAmendmentKey: z.string().min(1).nullable().optional(),
    confirmation: AssetAnalysisConfirmationSchema.nullable().optional(),
    effectiveCreatorMetadata: CompleteCreatorMetadataSchema.nullable().optional(),
    effectiveCreatorRevision: z.string().min(1).nullable().optional(),
    analysis: AssetAnalysisResponseSchema
  })
  .strict()
  .superRefine((projection, ctx) => {
    const {
      state,
      category,
      categorySource,
      manualResolution,
      creatorCategoryOverride,
      creatorCategoryAmendmentKey,
      confirmation,
      effectiveCreatorMetadata,
      effectiveCreatorRevision,
      analysis
    } = projection
    const successful = analysis.status === AnalysisStatusEnum.enum.succeeded
    const terminalFailure = analysis.status === AnalysisStatusEnum.enum.failed && analysis.canRetry === false

    if ((category === null) !== (categorySource === null)) {
      ctx.addIssue({
        code: 'custom',
        path: ['category'],
        message: 'category and categorySource must be provided together'
      })
    }
    if (analysis.status === AnalysisStatusEnum.enum.failed && categorySource === CategorySourceEnum.enum.nova) {
      ctx.addIssue({ code: 'custom', path: ['categorySource'], message: 'failed analysis cannot have Nova provenance' })
    }

    if (state === TriageStateEnum.enum.reading) {
      if (
        successful ||
        category !== null ||
        categorySource !== null ||
        manualResolution !== null ||
        creatorCategoryOverride !== null ||
        creatorCategoryAmendmentKey != null ||
        confirmation != null ||
        effectiveCreatorMetadata != null ||
        effectiveCreatorRevision != null
      ) {
        ctx.addIssue({ code: 'custom', path: ['state'], message: 'Reading projections cannot carry ready provenance' })
      }
      return
    }

    if (state === TriageStateEnum.enum.needs_confirmation) {
      if (
        !successful ||
        category !== analysis.category ||
        categorySource !== CategorySourceEnum.enum.nova ||
        manualResolution !== null ||
        creatorCategoryOverride !== null ||
        creatorCategoryAmendmentKey != null ||
        confirmation != null ||
        effectiveCreatorMetadata == null ||
        effectiveCreatorRevision == null
      ) {
        ctx.addIssue({
          code: 'custom',
          path: ['state'],
          message: 'Needs confirmation projections require a complete unconfirmed successful analysis'
        })
      }
      return
    }

    if (state === TriageStateEnum.enum.rejected) {
      if (
        !successful ||
        category !== null ||
        categorySource !== null ||
        manualResolution !== null ||
        creatorCategoryOverride !== null ||
        creatorCategoryAmendmentKey != null ||
        confirmation != null ||
        effectiveCreatorMetadata != null ||
        effectiveCreatorRevision != null
      ) {
        ctx.addIssue({
          code: 'custom',
          path: ['state'],
          message: 'Rejected projections require a successful analysis without effective eligibility provenance'
        })
      }
      return
    }

    if (state === TriageStateEnum.enum.held_back) {
      if (!terminalFailure) {
        ctx.addIssue({
          code: 'custom',
          path: ['analysis'],
          message: 'Held back projections require terminal failed analysis'
        })
      }
      if (
        category !== null ||
        categorySource !== null ||
        manualResolution !== null ||
        creatorCategoryOverride !== null ||
        creatorCategoryAmendmentKey != null ||
        confirmation != null
      ) {
        ctx.addIssue({
          code: 'custom',
          path: ['state'],
          message: 'Held back projections cannot carry effective provenance'
        })
      }
      return
    }

    if (category === null || categorySource === null) {
      ctx.addIssue({
        code: 'custom',
        path: ['state'],
        message: 'Ready projections require an effective category and source'
      })
      return
    }

    if (categorySource === CategorySourceEnum.enum.nova) {
      if (
        !successful ||
        analysis.category !== category ||
        manualResolution !== null ||
        creatorCategoryOverride !== null ||
        creatorCategoryAmendmentKey != null ||
        confirmation != null
      ) {
        ctx.addIssue({
          code: 'custom',
          path: ['categorySource'],
          message: 'Nova provenance requires the successful classified category'
        })
      }
      return
    }

    if (categorySource === CategorySourceEnum.enum.creator) {
      const manualCreatorReady = terminalFailure && manualResolution !== null
      const hasCategoryAmendment = creatorCategoryAmendmentKey != null
      if (
        (!successful && !manualCreatorReady) ||
        (confirmation != null && creatorCategoryOverride !== null) ||
        (confirmation == null && creatorCategoryOverride === null && !hasCategoryAmendment) ||
        (manualCreatorReady &&
          (confirmation != null || creatorCategoryOverride !== null || !hasCategoryAmendment)) ||
        (confirmation != null &&
          (confirmation.generation !== analysis.generation ||
            confirmation.attempt !== analysis.attempt ||
            effectiveCreatorMetadata == null ||
            effectiveCreatorRevision == null)) ||
        (creatorCategoryOverride !== null &&
          (creatorCategoryOverride.generation !== analysis.generation ||
            creatorCategoryOverride.attempt !== analysis.attempt)) ||
        (!hasCategoryAmendment && creatorCategoryOverride !== null && creatorCategoryOverride.category !== category) ||
        (!hasCategoryAmendment && confirmation != null && confirmation.category !== category) ||
        effectiveCreatorMetadata == null ||
        effectiveCreatorRevision == null
      ) {
        ctx.addIssue({
          code: 'custom',
          path: ['categorySource'],
          message: 'Creator provenance requires current immutable creator state'
        })
      }
      return
    }

    if (
      !terminalFailure ||
      manualResolution === null ||
      manualResolution.category !== category ||
      creatorCategoryOverride !== null ||
      creatorCategoryAmendmentKey != null ||
      confirmation != null ||
      effectiveCreatorMetadata == null ||
      effectiveCreatorRevision == null
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['categorySource'],
        message: 'Manual provenance requires a current manual resolution'
      })
    }
  })
export type AssetTriageProjection = z.infer<typeof AssetTriageProjectionSchema>

export const AssetProjectionSchema = AssetSchema.extend({
  thumbnailUrl: z.string().url().nullable().optional(),
  triage: AssetTriageProjectionSchema
}).strict()
export type AssetProjection = z.infer<typeof AssetProjectionSchema>

// RAL-229: list and detail carry the resolved tags so a tile never needs a second read per campaign.
export const TaggedAssetProjectionSchema = AssetProjectionSchema.extend({
  campaignTags: z.array(AssetCampaignTagSchema)
}).strict()
export type TaggedAssetProjection = z.infer<typeof TaggedAssetProjectionSchema>

export const AssetsListResponseSchema = z.object({ assets: z.array(TaggedAssetProjectionSchema) }).strict()
export type AssetsListResponse = z.infer<typeof AssetsListResponseSchema>

// "Posted where" on the asset detail: a published lane plus the name of the campaign that posted it.
export const AssetPostSchema = DeliveryHistoryEntrySchema.extend({ campaignName: z.string().nullable() })
export type AssetPost = z.infer<typeof AssetPostSchema>

export const AssetDetailResponseSchema = z
  .object({
    asset: TaggedAssetProjectionSchema,
    downloadUrl: z.string().url(),
    postedWhere: z.array(AssetPostSchema)
  })
  .strict()
export type AssetDetailResponse = z.infer<typeof AssetDetailResponseSchema>

export const AssetMutationResponseSchema = z.object({ asset: AssetProjectionSchema }).strict()
export type AssetMutationResponse = z.infer<typeof AssetMutationResponseSchema>

export const AcceptAssetResponseSchema = AssetDetailResponseSchema
export type AcceptAssetResponse = z.infer<typeof AcceptAssetResponseSchema>
